import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Topbar from "../../components/Topbar/Topbar";
import { loadGmHudConfig } from "../../utils/config";
import { logActivity } from "../../utils/activity";
import "./BookingPage.css";

const GAMES_CLOUD_BASE = (process.env.REACT_APP_GAMES_CLOUD_URL || "")
    .replace(/^ws/, "http")
    .replace(/ws$/, "");

export function api(path, options = {}) {
    const token = localStorage.getItem("accessToken");
    return fetch(`${GAMES_CLOUD_BASE}${path}`, {
        ...options,
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    });
}

// The chosen room follows the GM from one booking page to the other: the same room is nearly always
// meant, and re-picking it on every page change is friction for no gain.
const ROOM_KEY = "bookingRoom";

// BookingShell is what the walk-in page and the extra-players page have in common: the gate, the
// room list, and the room picker.
//
// requireBooking is the per-user "Booking" tick in the staff sheet. Creating a walk-in needs it
// while it is being rolled out; the extra-player price only reads, so it is open to everyone on
// shift. Either way this gate just stops the page rendering — games-cloud enforces the same rule,
// and the user's locations, on every call.
//
// children is called with the chosen room once there is one.
export default function BookingShell({ title, activity, requireBooking = true, children }) {
    const navigate = useNavigate();
    const [unauthorized, setUnauthorized] = useState(false);
    const [ready, setReady] = useState(false);
    const [rooms, setRooms] = useState([]);
    const [roomsError, setRoomsError] = useState("");
    const [calendarId, setCalendarId] = useState(() => sessionStorage.getItem(ROOM_KEY) || "");

    useEffect(() => {
        const email = localStorage.getItem("userEmail") || "";
        loadGmHudConfig().then(cfg => {
            const user = cfg?.users?.[email.toLowerCase()];
            if (!user || (requireBooking && !user.booking)) { setUnauthorized(true); return; }
            setReady(true);
            logActivity(activity);
        });
    }, [activity, requireBooking]);

    useEffect(() => {
        if (!ready) return;
        api("gm-booking/rooms")
            .then(r => (r.ok ? r.json() : Promise.reject(r.status)))
            .then(data => {
                const list = data.rooms || [];
                setRooms(list);
                setCalendarId(current => (list.some(r => r.calendar_id === current) ? current : list[0]?.calendar_id || ""));
            })
            .catch(() => setRoomsError("Kunne ikke hente rummene."));
    }, [ready]);

    useEffect(() => {
        if (calendarId) sessionStorage.setItem(ROOM_KEY, calendarId);
    }, [calendarId]);

    const room = rooms.find(r => r.calendar_id === calendarId) || null;
    const locations = useMemo(() => [...new Set(rooms.map(r => r.location))], [rooms]);

    if (unauthorized) return (
        <div style={{ padding: 40, color: "#e74c3c" }}>
            Ingen adgang. <button onClick={() => navigate("/")} style={{ marginLeft: 12, cursor: "pointer" }}>Tilbage</button>
        </div>
    );

    return (
        <>
            <Topbar />

            <div className="bk-content">
                <div className="bk-toolbar">
                    <label className="bk-field">
                        Rum
                        <select value={calendarId} onChange={e => setCalendarId(e.target.value)} disabled={!rooms.length}>
                            {locations.map(loc => (
                                <optgroup key={loc} label={loc}>
                                    {rooms.filter(r => r.location === loc).map(r => (
                                        <option key={r.calendar_id} value={r.calendar_id}>{r.name}</option>
                                    ))}
                                </optgroup>
                            ))}
                        </select>
                    </label>
                </div>

                {roomsError && <div className="bk-error">{roomsError}</div>}
                {ready && !roomsError && rooms.length === 0 && <div className="bk-empty">Ingen rum på dine lokationer.</div>}

                {room && children(room)}
            </div>
        </>
    );
}
